import React, { useState } from 'react';
import css from './ShippingDetailsForm.module.css';
import {
  FieldCheckbox,
  FieldCurrencyInput,
  FieldRadioButton,
  FieldSelect,
  FieldTextInput,
  H3,
  Modal,
} from '../../../components';
import PhoneVerification from '../../../components/PhoneVerification';
import { FormattedMessage } from '../../../util/reactIntl';
import { required } from '../../../util/validators';
import appSettings from '../../../config/settings';
import { SHIPPING, YES } from '../../../util/constants';
import { isPhoneVerifiedForUser } from '../../../util/phoneNumber';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { Field } from 'react-final-form';
import { useConfiguration } from '../../../context/configurationContext';
import classNames from 'classnames';
import ShippingOptions from './ShippingOptions';

const noop = () => {};
const DEFAULT_PHONE_COUNTRY = 'US';

const ShippingAddress = ({
  currentUser,
  form,
  values,
  intl,
  formId,
  countryCodes,
  TTlWeightWrapper,
  isTutulist,
  onManageDisableScrolling = noop,
  onRefreshCurrentUser = noop,
  onSaveVerifiedPhoneNumber = noop,
}) => {
  const config = useConfiguration();
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const marketplaceCurrency = config.currency;
  const { threshold, invalidPhoneValue, phoneNumber, shippingType } = values || {};
  const hasPhoneNumber = Boolean(phoneNumber);
  const phoneNumberVerified = hasPhoneNumber && isPhoneVerifiedForUser(currentUser, phoneNumber);
  const shouldShowThreshold = shippingType === SHIPPING;
  const shippingClasses = classNames(css.deliveryOption);

  return (
    <div className={css.defaultPackageAndDetails}>
      {isTutulist && !TTlWeightWrapper && (
        <ShippingOptions form={form} values={values} intl={intl} />
      )}
      {shouldShowThreshold ? (
        <div className={css.shippingPayment}>
          <label htmlFor="threshold">
            {intl.formatMessage({
              id: 'ShippingDetailsForm.doYouWantToAddFreeShippingThreshold',
            })}
          </label>
          <div className={css.actions}>
            {[{ key: 'no', label: 'No' }, { key: 'yes', label: 'Yes' }].map(item => (
              <FieldRadioButton
                key={item.key}
                id={item.key}
                name="threshold"
                label={item.label}
                value={item.key}
              />
            ))}
          </div>
        </div>
      ) : null}
      {threshold === YES && shouldShowThreshold ? (
        <div className={classNames(css.formRow, css.thresholdAmount)}>
          <div className={css.formFld}>
            <div className={shippingClasses}>
              <FieldCurrencyInput
                id="thresholdAmount"
                key="thresholdAmount"
                name="thresholdAmount"
                className={classNames(css.input)}
                label={intl.formatMessage({
                  id: 'ShippingDetailsForm.thresholdAmountLabel',
                })}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                validate={required(
                  intl.formatMessage({
                    id: 'ShippingDetailsForm.thresholdAmountRequired',
                  })
                )}
                placeholder={'$0.00'}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className={css.defaultPackage}>
        <label htmlFor="packageSize" className={css.packageSize}>
          {intl.formatMessage({
            id: 'ShippingDetailsForm.packageSize',
          })}
        </label>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.input}
              type="number"
              id="length"
              name="length"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.length',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.lengthPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.lengthRequired',
                })
              )}
            />
          </div>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.input}
              type="number"
              id="width"
              name="width"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.width',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.widthPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.widthRequired',
                })
              )}
            />
          </div>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.input}
              type="number"
              id="height"
              name="height"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.height',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.heightPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.heightRequired',
                })
              )}
            />
          </div>
        </div>
      </div>
      <div className={css.shippingAddressSec}>
        <H3 as="h3" className={css.sectionTitle}>
          <FormattedMessage id="ShippingDetailsForm.shippingAddressTitle" />
        </H3>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.firstInput}
              id={formId ? `${formId}.address` : 'address'}
              type="text"
              name="line1"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.streetAddress',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.streetAddressPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.streetAddressRequired',
                })
              )}
            />
          </div>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.secondInput}
              id={formId ? `${formId}.apt` : 'apt'}
              type="text"
              name="line2"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.buildingNumber',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.buildingNumberPlaceholder',
              })}
              // validate={required(
              //   intl.formatMessage({
              //     id: 'ShippingDetailsForm.buildingNumberRequired',
              //   })
              // )}
            />
          </div>
        </div>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.firstInput}
              id={formId ? `${formId}.city` : 'city'}
              type="text"
              name="city"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.city',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.cityPlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.cityRequired',
                })
              )}
            />
          </div>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.secondInput}
              id={formId ? `${formId}.zip` : 'zip'}
              type="text"
              name="zip"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.postalCode',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.postalCodePlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.postalCodeRequired',
                })
              )}
            />
          </div>
        </div>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <FieldTextInput
              className={css.firstInput}
              id={formId ? `${formId}.state` : 'state'}
              type="text"
              name="state"
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.state',
              })}
              placeholder={intl.formatMessage({
                id: 'ShippingDetailsForm.statePlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.stateRequired',
                })
              )}
            />
          </div>
          <div className={css.formFld}>
            <FieldSelect
              id={formId ? `${formId}.country` : 'country'}
              name="country"
              className={css.field}
              label={intl.formatMessage({
                id: 'ShippingDetailsForm.countryLabel',
              })}
              initialValue={values?.country?.code || values?.country || 'US'}
              validate={required(
                intl.formatMessage({
                  id: 'ShippingDetailsForm.countryRequired',
                })
              )}
            >
              <option disabled value="">
                {intl.formatMessage({
                  id: 'ShippingDetailsForm.countryPlaceholder',
                })}
              </option>
              {countryCodes.map(country => {
                return (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                );
              })}
            </FieldSelect>
          </div>
        </div>
        <div className={classNames(css.formRow, css.phoneNumber)}>
          <div className={css.formFld}>
            <FieldCheckbox
              id="enableNonUSOrders"
              className={css.deliveryCheckbox}
              name="enableNonUSOrders"
              label={intl.formatMessage({ id: 'ShippingDetailsForm.enableNonUSOrders' })}
              value="yes"
            />
          </div>
        </div>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <label className={css.phoneNumberLabel}>
              {intl.formatMessage({
                id: 'ShippingDetailsForm.phone',
              })}
            </label>
            <Field name="phoneNumber">
              {({ input }) => (
                <div>
                  <div className={css.phoneInputWithAction}>
                    <PhoneInput
                      {...input}
                      placeholder={intl.formatMessage({
                        id: 'ShippingDetailsForm.phonePlaceholder',
                      })}
                      defaultCountry={DEFAULT_PHONE_COUNTRY}
                      onChange={value => {
                        input.onChange(value);
                        if (value && isValidPhoneNumber(value)) {
                          form.change('invalidPhoneValue', false);
                        } else {
                          form.change('invalidPhoneValue', true);
                        }
                      }}
                    />
                    {hasPhoneNumber ? (
                      phoneNumberVerified ? (
                        <span className={css.phoneVerifiedInline}>Verified</span>
                      ) : (
                        <button
                          className={css.phoneVerifyButton}
                          type="button"
                          onClick={() => setShowPhoneVerificationModal(true)}
                        >
                          Verify
                        </button>
                      )
                    ) : null}
                  </div>
                  <Modal
                    id="ShippingDetailsForm.phoneVerificationModal"
                    isOpen={showPhoneVerificationModal}
                    onClose={() => setShowPhoneVerificationModal(false)}
                    onManageDisableScrolling={onManageDisableScrolling}
                    modalTitle="Verify phone number"
                  >
                    <PhoneVerification
                      currentUser={currentUser}
                      initialPhoneNumber={phoneNumber || ''}
                      onPhoneNumberChange={value => {
                        input.onChange(value);
                        form.change('invalidPhoneValue', !(value && isValidPhoneNumber(value)));
                      }}
                      onVerified={async data => {
                        const verifiedPhoneNumber = data?.phoneNumber || phoneNumber || '';
                        input.onChange(verifiedPhoneNumber);
                        form.change('invalidPhoneValue', false);
                        await onSaveVerifiedPhoneNumber({
                          ...values,
                          phoneNumber: verifiedPhoneNumber,
                        });
                        await onRefreshCurrentUser();
                        setShowPhoneVerificationModal(false);
                      }}
                      showFrame={false}
                      showTitle={false}
                      showVerifiedSummary={false}
                    />
                  </Modal>
                </div>
              )}
            </Field>
            {invalidPhoneValue && phoneNumber && (
              <span className={css.invalidPhoneValue}>
                {' '}
                {intl.formatMessage({
                  id: 'ShippingDetailsForm.inivalidPhoneNumber',
                })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingAddress;
